'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { IoSearchOutline, IoChevronDown } from 'react-icons/io5'
import {
  DropdownGroupOption,
  CustomOptionProps,
  CustomSingleValueProps,
} from '@/types'

import Select, {
  components,
  ControlProps,
  PlaceholderProps,
  DropdownIndicatorProps,
  MenuProps,
} from 'react-select'

interface SideSelectProps {
  selectionId: string
  value?: any
  dropdownLabel: string
  dropdownLeftSide?: boolean
  canSearchDropdown?: boolean
  options: { value: string; label: string }[] | DropdownGroupOption[]
  onSelectValue: (value: any) => void
}

export default function SideSelect({
  selectionId,
  value,
  dropdownLabel,
  dropdownLeftSide,
  canSearchDropdown = false,
  options,
  onSelectValue,
}: SideSelectProps) {
  const [isFocused, setIsFocused] = useState(false)

  const ControlComponent = (props: ControlProps<any, any, any>) => {
    return (
      <components.Control
        className={`${dropdownLeftSide ? '!rounded-l-lg !rounded-r-none' : '!rounded-l-none !rounded-r-lg'} !cursor-pointer w-40 h-[42px] !bg-gray-100 `}
        {...props}
      />
    )
  }

  const Placeholder = (props: PlaceholderProps<any, any, any>) => {
    return (
      <components.Placeholder
        className="text-sm !text-gray-900 text-center"
        {...props}
      />
    )
  }

  const SingleValue = ({ children, ...props }: CustomSingleValueProps) => {
    return (
      <components.SingleValue {...props}>
        <div className="flex gap-2 items-center justify-center">
          {props.data.icon && (
            <Image
              src={props.data?.icon}
              className="w-5 h-4"
              alt=""
              width={0}
              height={0}
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          )}
          <span>{children}</span>
        </div>
      </components.SingleValue>
    )
  }

  const IndicatorSeparator = () => {
    return <></>
  }

  const DropdownIndicator = (props: DropdownIndicatorProps<any, any, any>) => {
    return (
      <components.DropdownIndicator {...props}>
        <IoChevronDown className="text-gray-900 cursor-pointer" />
      </components.DropdownIndicator>
    )
  }

  const Menu = ({ selectProps, ...props }: MenuProps<any, any, any>) => {
    const { onInputChange, inputValue } = selectProps

    return (
      <components.Menu
        className="min-w-52 px-2 border border-gray-200  shadow-lg !rounded-lg"
        {...props}
        selectProps={selectProps}
      >
        {canSearchDropdown && (
          <div className="text-gray-500 relative">
            <input
              onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                e.stopPropagation()
                setIsFocused(true)
              }}
              onChange={(e) => {
                // @ts-ignore
                onInputChange(e.currentTarget.value, { action: 'input-change' })
              }}
              value={inputValue}
              className="pl-6 text-sm border border-gray-300 rounded-lg relative w-full mt-2"
              placeholder="Search"
              type="text"
            />
            <div className="absolute top-0 mt-5 ml-2">
              <IoSearchOutline width={16} height={16} />
            </div>
          </div>
        )}
        <span className="text-left text-sm text-gray-900">
          {props.children}
        </span>
      </components.Menu>
    )
  }

  const Option = (props: CustomOptionProps) => {
    return (
      <components.Option {...props}>
        <div className="flex gap-2 items-center">
          {props.data.icon && (
            <Image
              src={props.data.icon}
              className="w-5 h-4"
              alt=""
              width={0}
              height={0}
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          )}
          <span>{props.children}</span>
        </div>
      </components.Option>
    )
  }

  return (
    <div>
      <Select
        instanceId={selectionId}
        isSearchable={false}
        options={options}
        defaultValue={value}
        placeholder={dropdownLabel}
        styles={{
          container: (base) => ({
            ...base,
            fontSize: 14,
            color: '#111928',
            textAlign: 'center',
          }),
          groupHeading: (base) => ({
            ...base,
            color: '#111928',
          }),
        }}
        onChange={(e) => {
          setIsFocused(false)
          onSelectValue(e)
        }}
        {...{
          menuIsOpen: isFocused || undefined,
          isFocused: isFocused || undefined,
        }}
        components={{
          Control: ControlComponent,
          Placeholder,
          SingleValue,
          IndicatorSeparator,
          DropdownIndicator,
          Menu,
          Option,
        }}
      />
    </div>
  )
}
